import type {
  NotificationHoursPolicy,
  SetNotificationHours,
  NotificationHoursResult,
} from "@reminder-tracker/backend-contracts";
import { makeFunctionReference } from "convex/server";
import type {
  DiagnosticChannel,
  DiagnosticView,
  DiagnosticRequest,
  DiagnosticResult,
  DeliveryPolicyReason,
  DeliveryOperationsRequest,
  DeliveryOperationsEvidence,
  CatalogueInventoryRequest,
  CatalogueInventoryPage,
  CatalogueMonthlyReport,
  CatalogueMonthlyReportRequest,
  DecideCatalogueSuggestion,
  CatalogueSuggestionResult,
  CatalogueSuggestionQueueRequest,
  OwnCatalogueSuggestions,
  CatalogueAdministrationCapabilities,
  CatalogueVersionRequest,
  CatalogueVersionPreview,
  CatalogueVersionListRequest,
  CatalogueVersionPage,
  CatalogueDraftCommand,
  CatalogueTransitionCommand,
  CatalogueAdministrationResult,
  AdministrationSelfContext,
  AndroidPushAdministrationOverview,
  AndroidPushPolicyResult,
  IosPushPolicyResult,
  IosPushPolicyStatus,
  EmailAdministrationOverview,
  EmailChannelPolicyResult,
  AdministratorMfaChallengeResult,
  AdministratorMfaEnrollmentResult,
  AdministratorMfaStatus,
  AdministratorMfaVerificationResult,
  BundleAdministrationArea,
  BundleAdministrationTaskPage,
  BundleAdministrationVersion,
  BundleAdministrationResult,
  BundleDraftCommand,
  BundleReviewSubmission,
  BundleReviewDecisionCommand,
  BundleReleaseCandidateCommand,
  BundleReleasePublishCommand,
  BundleReleasePreview,
  BundleReleaseResult,
  BundleRetirementEntry,
  BundleRetirementPreview,
  BundleRetirementCommand,
  BundleRetirementResult,
  BundleWithdrawalCommand,
  BundleWithdrawalPreview,
  BundleWithdrawalResult,
  BundleWithdrawalReviewCommand,
  BundleWithdrawalReviewResult,
  BundleWithdrawalReviewTaskPage,
  Goal2AdministrationCapabilityProjection,
  Goal2AggregateReportRequest,
  Goal2AggregateReportResult,
  TimingRecommendationPage,
  TimingRecommendationDecisionCommand,
  TimingRecommendationDecisionResult,
  TimingRecommendationConditionVerificationCommand,
  TimingRecommendationConditionVerificationResult,
  OperationsGroupPage,
  OperationsGroupPageRequest,
  OperationsInvestigation,
  ServiceOperationsCapabilities,
  ServiceOperationsOverview,
  OperationsGroupDetail,
  ServiceContainmentCommand,
  ServiceContainmentReceipt,
  ServiceContainmentReview,
  ServiceContainmentProtectedNote,
  ServiceContainmentNoticeObligationPage,
  ServiceNoticeCommandResult,
  ActiveServiceNotices,
  ServiceNoticeDraftInput,
  ServiceNoticePreview,
  EvaluationRecoveryContinuation,
  EvaluationRecoveryPreview,
  EvaluationRecoveryExecuteRequest,
  EvaluationRecoveryJob,
  EvaluationRecoveryCommandResult,
  MaskedSupportQueuePage,
  OperatorSupportCase,
  SupportAssignmentReceipt,
  EligibleSupportOperator,
  SupportCaseReceipt,
  SupportMetrics,
  DataLifecycleAdministrationSnapshot,
  DataLifecycleWorkRegister,
  DataProcessingReviewDecision,
  DataProcessingReviewRegister,
  WhatsAppAllowanceCommandResult,
  WhatsAppAllowancePolicy,
  WhatsAppOperatorExposureView,
  WhatsAppOperatorPolicy,
  WhatsAppOperatorReadiness,
} from "@reminder-tracker/backend-contracts";
import type { SupportQueueFilters } from "@reminder-tracker/backend-contracts";
import type { WhatsAppOperatorPolicyInput } from "@reminder-tracker/domain";

export const getWhatsAppOperatorReadiness = makeFunctionReference<
  "query",
  { correlationId: string },
  WhatsAppOperatorReadiness
>("whatsappOperations:getReadiness");

export const getWhatsAppOperatorExposure = makeFunctionReference<
  "query",
  { correlationId: string },
  WhatsAppOperatorExposureView
>("whatsappOperations:getExposure");

export const configureWhatsAppOperatorPolicy = makeFunctionReference<
  "mutation",
  WhatsAppOperatorPolicyInput & {
    expectedRevision: number | null;
    reason: DeliveryPolicyReason;
    commandKey: string;
    correlationId: string;
  },
  { policy: WhatsAppOperatorPolicy; replayed: boolean; correlationId: string }
>("whatsappOperations:configure");

export const getWhatsAppAllowancePolicy = makeFunctionReference<
  "query",
  { correlationId: string },
  WhatsAppAllowancePolicy | null
>("whatsappAllowances:getPolicy");

export const configureWhatsAppAllowancePolicy = makeFunctionReference<
  "mutation",
  {
    expectedRevision: number | null;
    freeUnits: number;
    effectiveAt: number;
    subscriptionsAvailable: false;
    topUpsAvailable: false;
    commandKey: string;
    correlationId: string;
  },
  WhatsAppAllowanceCommandResult
>("whatsappAllowances:configure");

export const getDiagnosticView = makeFunctionReference<
  "query",
  { correlationId: string; channel: DiagnosticChannel },
  DiagnosticView
>("deliveryDiagnostics:getView");
export const requestDiagnosticTest = makeFunctionReference<
  "mutation",
  DiagnosticRequest,
  DiagnosticResult
>("deliveryDiagnostics:requestTest");

export const getDeliveryCapabilities = makeFunctionReference<
  "query",
  { correlationId: string },
  { canManagePolicy: boolean; canSendDiagnostic: boolean }
>("deliveryOperations:getCapabilities");

export const getDeliveryOperationsEvidence = makeFunctionReference<
  "query",
  DeliveryOperationsRequest,
  DeliveryOperationsEvidence
>("deliveryOperations:getEvidence");
export const listSupportQueue = makeFunctionReference<
  "query",
  Readonly<{
    correlationId: string;
    view:
      | "needs-attention"
      | "unassigned"
      | "waiting-for-you"
      | "resolved"
      | "closed"
      | "assigned-to-me";
    sort:
      | "target"
      | "received-newest"
      | "received-oldest"
      | "updated-newest"
      | "updated-oldest"
      | "severity";
    filters: SupportQueueFilters | null;
    exactCaseCode: string | null;
    paginationOpts: Readonly<{ cursor: string | null; numItems: number }>;
  }>,
  MaskedSupportQueuePage
>("supportQueue:list");
export const claimSupportCase = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    requestKey: string;
    caseCode: string;
    expectedAssignmentRevision: number;
    expectedWorkflowRevision: number;
  }>,
  SupportAssignmentReceipt
>("supportAssignment:claim");
export const syncOwnSupportEligibility = makeFunctionReference<
  "mutation",
  Readonly<{ correlationId: string }>,
  Readonly<{ eligibleUntilEpochMilliseconds: number; correlationId: string }>
>("supportAssignment:syncOwnEligibility");
export const listEligibleSupportOperators = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  ReadonlyArray<EligibleSupportOperator>
>("supportAssignment:listEligibleOperators");
export const reassignOrReleaseSupportCase = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    requestKey: string;
    caseCode: string;
    targetOperatorReference: string | null;
    reason:
      "capacity-handover" | "specialist-needed" | "operator-unavailable" | "incorrect-assignment";
    expectedAssignmentRevision: number;
  }>,
  SupportAssignmentReceipt
>("supportAssignment:reassignOrRelease");
export const openAssignedSupportCase = makeFunctionReference<
  "mutation",
  Readonly<{ correlationId: string; requestKey: string; caseCode: string }>,
  OperatorSupportCase
>("supportAssignment:openAssignedCase");
export const triageAssignedSupportCase = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    requestKey: string;
    caseCode: string;
    category: OperatorSupportCase["category"];
    severity: OperatorSupportCase["severity"];
    reason: "intake-assessment" | "new-evidence" | "specialist-guidance";
    expectedWorkflowRevision: number;
    expectedAssignmentRevision: number;
  }>,
  Readonly<{
    caseCode: string;
    category: OperatorSupportCase["category"];
    severity: OperatorSupportCase["severity"];
    state: OperatorSupportCase["state"];
    workflowRevision: number;
    originalTargetAtEpochMilliseconds: number;
    currentTargetAtEpochMilliseconds: number;
    firstReviewedAtEpochMilliseconds: number;
    priorTargetMissed: boolean;
    correlationId: string;
    replayed: boolean;
  }>
>("supportWorkflow:triage");
export const transitionAssignedSupportCase = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    requestKey: string;
    caseCode: string;
    transition: "ask-requester" | "resolve";
    message: string;
    expectedWorkflowRevision: number;
  }>,
  SupportCaseReceipt
>("supportWorkflow:transition");
export const getSupportMetrics = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  SupportMetrics
>("supportMetrics:get");

export const getServiceOperationsCapabilities = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  ServiceOperationsCapabilities
>("serviceOperations:getCapabilities");

export const getServiceOperationsOverview = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string; window: "24-hours" | "7-days" }>,
  ServiceOperationsOverview
>("serviceOperations:getOverview");

export const getDataLifecycleAdministrationSnapshot = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string; window: "today" | "7-days" | "30-days" }>,
  DataLifecycleAdministrationSnapshot
>("dataLifecycle:getAdministrationSnapshot");
export const getDataLifecycleWorkItems = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  DataLifecycleWorkRegister
>("dataLifecycleAdministration:listWorkItems");
export const getDataProcessingReviews = makeFunctionReference<
  "query",
  { correlationId: string },
  DataProcessingReviewRegister
>("dataLifecycleAdministration:listProcessingReviews");
export const decideDataProcessingReview = makeFunctionReference<
  "mutation",
  {
    casePublicId: string;
    decision: "accepted" | "refused";
    reason:
      | "customer-choice-applied"
      | "essential-service-processing-required"
      | "mandatory-rights-processing-required";
    commandKey: string;
    correlationId: string;
  },
  DataProcessingReviewDecision
>("dataLifecycleAdministration:decideProcessingReview");
export const retryDataLifecycleMilestone = makeFunctionReference<
  "mutation",
  {
    operationPublicId: string;
    milestoneKind: DataLifecycleWorkRegister["items"][number]["milestoneKind"];
    commandKey: string;
    correlationId: string;
  },
  { state: "pending"; replayed: boolean }
>("dataLifecycleAdministration:retryMilestone");
export const recordDataLifecycleExternalEvidence = makeFunctionReference<
  "mutation",
  {
    operationPublicId: string;
    milestoneKind: "processor-data-reconciled" | "backup-reconciliation-verified";
    outcome: "verified" | "not-applicable";
    acknowledgementReference: string;
    commandKey: string;
    correlationId: string;
  },
  { state: "verified" | "not-applicable"; operationCompleted: boolean }
>("dataLifecycleAdministration:recordExternalEvidence");

export const openServiceOperationsInvestigation = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    purpose: "service-health" | "incident-follow-up";
    window: "24-hours" | "7-days";
  }>,
  OperationsInvestigation
>("serviceOperations:openInvestigation");

export const listServiceOperationsGroups = makeFunctionReference<
  "mutation",
  OperationsGroupPageRequest,
  OperationsGroupPage
>("serviceOperations:listGroups");

export const getServiceOperationsGroupDetail = makeFunctionReference<
  "mutation",
  Readonly<{ correlationId: string; investigationId: string; reference: string }>,
  OperationsGroupDetail
>("serviceOperations:getGroupDetail");

export const reviewServiceContainment = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    target: Readonly<{ kind: "group" | "person"; reference: string }>;
    desiredMode: "active" | "restricted" | "locked";
  }>,
  ServiceContainmentReview
>("serviceContainment:review");

export const applyServiceContainment = makeFunctionReference<
  "mutation",
  ServiceContainmentCommand,
  ServiceContainmentReceipt
>("serviceContainment:apply");
export const readServiceContainmentProtectedNote = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    target: Readonly<{ kind: "group" | "person"; reference: string }>;
  }>,
  ServiceContainmentProtectedNote
>("serviceContainment:readProtectedNote");
export const listDueServiceContainmentNoticeObligations = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  ServiceContainmentNoticeObligationPage
>("serviceContainment:listDueNoticeObligations");
export const reviewServiceContainmentNoticeObligation = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    commandKey: string;
    reference: string;
    expectedRevision: number;
    decision: "delayed-for-safety" | "unavailable" | "resolved";
  }>,
  Readonly<{
    correlationId: string;
    reference: string;
    state: string;
    revision: number;
    replayed: boolean;
  }>
>("serviceContainment:reviewNoticeObligation");

export const saveServiceNoticeDraft = makeFunctionReference<
  "mutation",
  ServiceNoticeDraftInput,
  ServiceNoticeCommandResult
>("serviceNotices:saveDraft");

export const listActiveServiceNotices = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  ActiveServiceNotices
>("serviceNotices:listActiveForAdministration");

export const previewServiceNotice = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string; incidentId: string; revision: number }>,
  ServiceNoticePreview
>("serviceNotices:preview");

export const publishServiceNotice = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    commandKey: string;
    incidentId: string;
    expectedRevision: number;
    previewDigest: string;
  }>,
  ServiceNoticeCommandResult
>("serviceNotices:publish");

export const resolveServiceNotice = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    commandKey: string;
    incidentId: string;
    expectedRevision: number;
  }>,
  ServiceNoticeCommandResult
>("serviceNotices:resolve");

export const getCatalogueInventory = makeFunctionReference<
  "query",
  CatalogueInventoryRequest,
  CatalogueInventoryPage
>("catalogueVersions:inventory");

export const getCatalogueMonthlyReport = makeFunctionReference<
  "action",
  CatalogueMonthlyReportRequest,
  CatalogueMonthlyReport
>("catalogueReporting:getMonthly");

export const getCatalogueSuggestionQueue = makeFunctionReference<
  "action",
  CatalogueSuggestionQueueRequest,
  OwnCatalogueSuggestions
>("catalogueSuggestions:getReviewQueue");
export const decideCatalogueSuggestion = makeFunctionReference<
  "mutation",
  DecideCatalogueSuggestion,
  CatalogueSuggestionResult
>("catalogueSuggestions:decide");

export const getCatalogueCapabilities = makeFunctionReference<
  "query",
  { correlationId: string },
  CatalogueAdministrationCapabilities
>("catalogueVersions:getCapabilities");
export const getCatalogueVersion = makeFunctionReference<
  "query",
  CatalogueVersionRequest,
  CatalogueVersionPreview
>("catalogueVersions:preview");
export const listCatalogueVersions = makeFunctionReference<
  "query",
  CatalogueVersionListRequest,
  CatalogueVersionPage
>("catalogueVersions:listVersions");
export const saveCatalogueDraft = makeFunctionReference<
  "mutation",
  CatalogueDraftCommand,
  CatalogueAdministrationResult
>("catalogueVersions:saveDraft");
export const transitionCatalogueVersion = makeFunctionReference<
  "mutation",
  CatalogueTransitionCommand,
  CatalogueAdministrationResult
>("catalogueVersions:transition");

export const getGoal2AdministrationCapabilities = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  Goal2AdministrationCapabilityProjection
>("goal2Administration:getCapabilities");

export const getGoal2MonthlyReport = makeFunctionReference<
  "action",
  Goal2AggregateReportRequest,
  Goal2AggregateReportResult
>("goal2Reporting:getMonthly");

export const listTimingRecommendations = makeFunctionReference<
  "mutation",
  Readonly<{ correlationId: string }>,
  TimingRecommendationPage
>("timingRecommendations:list");

export const decideTimingRecommendation = makeFunctionReference<
  "mutation",
  TimingRecommendationDecisionCommand,
  TimingRecommendationDecisionResult
>("timingRecommendations:decide");

export const verifyTimingRecommendationCondition = makeFunctionReference<
  "mutation",
  TimingRecommendationConditionVerificationCommand,
  TimingRecommendationConditionVerificationResult
>("timingRecommendations:verifyCondition");

export const listBundleAdministrationTasks = makeFunctionReference<
  "query",
  Readonly<{ area: BundleAdministrationArea; correlationId: string; cursor?: string | null }>,
  BundleAdministrationTaskPage
>("bundleAdministration:listTasks");

export const getBundleAdministrationVersion = makeFunctionReference<
  "query",
  Readonly<{
    area: BundleAdministrationArea;
    bundleId: string;
    version: number;
    correlationId: string;
  }>,
  BundleAdministrationVersion
>("bundleAdministration:preview");

export const saveBundleDraft = makeFunctionReference<
  "mutation",
  BundleDraftCommand,
  BundleAdministrationResult
>("bundleAdministration:saveDraft");

export const submitBundleReview = makeFunctionReference<
  "mutation",
  BundleReviewSubmission,
  BundleAdministrationResult
>("bundleAdministration:submitReview");

export const decideBundleReview = makeFunctionReference<
  "mutation",
  BundleReviewDecisionCommand,
  BundleAdministrationResult
>("bundleAdministration:decideReview");

export const saveBundleReleaseCandidate = makeFunctionReference<
  "mutation",
  BundleReleaseCandidateCommand,
  BundleReleaseResult
>("bundleReleases:saveCandidate");

export const previewBundleRelease = makeFunctionReference<
  "query",
  Readonly<{ releaseId: string; correlationId: string }>,
  BundleReleasePreview
>("bundleReleases:preview");

export const publishBundleRelease = makeFunctionReference<
  "mutation",
  BundleReleasePublishCommand,
  BundleReleaseResult
>("bundleReleases:publish");

export const publishCompletionProfileCandidate = makeFunctionReference<
  "mutation",
  Readonly<{
    candidateId: string;
    approvalReference: string;
    confirmed: boolean;
    commandKey: string;
    correlationId: string;
  }>,
  Readonly<{
    candidateId: string;
    publishedBindingCount: number;
    revision: number;
    correlationId: string;
    replayed: boolean;
  }>
>("completionProfileReleases:publishApprovedCandidate");

export const publishEverydayCompletionBindings = makeFunctionReference<
  "mutation",
  Readonly<{
    candidateId: "scrum-427-everyday-completion-v1";
    approvalReference: string;
    confirmed: boolean;
    commandKey: string;
    correlationId: string;
  }>,
  Readonly<{
    candidateId: string;
    publishedBindingCount: number;
    revision: number;
    correlationId: string;
    replayed: boolean;
  }>
>("completionProfileReleases:publishEverydayBindings");

export const previewBundleWithdrawal = makeFunctionReference<
  "query",
  Readonly<{ bundleId: string; bundleVersion: number; correlationId: string }>,
  BundleWithdrawalPreview
>("bundleWithdrawals:preview");

export const previewBundleRetirement = makeFunctionReference<
  "query",
  Readonly<{ entries: ReadonlyArray<BundleRetirementEntry>; correlationId: string }>,
  BundleRetirementPreview
>("bundleRetirements:preview");

export const retireBundleCatalogue = makeFunctionReference<
  "mutation",
  BundleRetirementCommand,
  BundleRetirementResult
>("bundleRetirements:retire");

export const withdrawBundleVersion = makeFunctionReference<
  "mutation",
  BundleWithdrawalCommand,
  BundleWithdrawalResult
>("bundleWithdrawals:withdraw");

export const listBundleWithdrawalReviewTasks = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  BundleWithdrawalReviewTaskPage
>("bundleWithdrawals:listReviewTasks");

export const reviewBundleWithdrawal = makeFunctionReference<
  "mutation",
  BundleWithdrawalReviewCommand,
  BundleWithdrawalReviewResult
>("bundleWithdrawals:review");

export const getAdministrationSelfContext = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  AdministrationSelfContext
>("administration:getSelfContext");

export const getAndroidPushAdministrationOverview = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  AndroidPushAdministrationOverview
>("pushNotifications:getAndroidPushOverview");

export const setAndroidPushPolicy = makeFunctionReference<
  "mutation",
  Readonly<{
    commandKey: string;
    correlationId: string;
    expectedRevision: number | null;
    mode: "disabled" | "test-only";
    reason: DeliveryPolicyReason;
  }>,
  AndroidPushPolicyResult
>("pushNotifications:setAndroidPushPolicy");

export const getIosPushPolicy = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  IosPushPolicyStatus
>("pushNotifications:getIosPushPolicy");

export const setIosPushPolicy = makeFunctionReference<
  "mutation",
  Readonly<{
    commandKey: string;
    correlationId: string;
    expectedRevision: number | null;
    mode: "disabled" | "test-only";
    reason: DeliveryPolicyReason;
  }>,
  IosPushPolicyResult
>("pushNotifications:setIosPushPolicy");

export const getAdministratorMfaStatus = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string; scope?: "operations" | "catalogue" }>,
  AdministratorMfaStatus
>("administratorMfa:getStatus");

export const startAdministratorMfaEnrollment = makeFunctionReference<
  "action",
  Readonly<{ correlationId: string }>,
  AdministratorMfaEnrollmentResult
>("administratorMfa:startEnrollment");

export const startAdministratorMfaChallenge = makeFunctionReference<
  "action",
  Readonly<{ correlationId: string }>,
  AdministratorMfaChallengeResult
>("administratorMfa:startChallenge");

export const verifyAdministratorMfaCode = makeFunctionReference<
  "action",
  Readonly<{ correlationId: string; code: string }>,
  AdministratorMfaVerificationResult
>("administratorMfa:verifyCode");

export const getEmailAdministrationOverview = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  EmailAdministrationOverview
>("notificationContacts:getEmailOverview");

export const setEmailChannelPolicy = makeFunctionReference<
  "mutation",
  Readonly<{
    commandKey: string;
    correlationId: string;
    expectedRevision: number | null;
    mode: "disabled" | "test-only";
    reason: DeliveryPolicyReason;
  }>,
  EmailChannelPolicyResult
>("notificationContacts:setEmailPolicy");

export const listRestrictedSupportCases = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string }>,
  ReadonlyArray<{
    caseCode: string;
    state: string;
    accessEpoch: number;
    workflowRevision: number;
    updatedAtEpochMilliseconds: number;
    specialistState: string;
  }>
>("supportRestricted:listRestricted");
export const claimRestrictedSupportCase = makeFunctionReference<
  "mutation",
  Record<string, unknown>,
  Readonly<{ caseCode: string; accessEpoch: number }>
>("supportRestricted:claimRestricted");
export const confirmSupportSpecialistHandoff = makeFunctionReference<
  "mutation",
  Record<string, unknown>,
  Readonly<{ caseCode: string; state: "confirmed" }>
>("supportRestricted:confirmSpecialistHandoff");
export const releaseRestrictedSupportCase = makeFunctionReference<
  "mutation",
  Record<string, unknown>,
  Readonly<{ caseCode: string; workflowRevision: number; state: "released" }>
>("supportRestricted:releaseRestricted");
export const revealRestrictedSupportContact = makeFunctionReference<
  "mutation",
  Record<string, unknown>,
  Readonly<{ caseCode: string; field: "email"; value: string; hideAtEpochMilliseconds: number }>
>("supportRestricted:revealContact");

export const previewEvaluationRecovery = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string; continuation: EvaluationRecoveryContinuation | null }>,
  EvaluationRecoveryPreview
>("evaluationRecovery:preview");
export const executeEvaluationRecovery = makeFunctionReference<
  "mutation",
  EvaluationRecoveryExecuteRequest,
  EvaluationRecoveryCommandResult
>("evaluationRecovery:execute");
export const getEvaluationRecoveryJob = makeFunctionReference<
  "query",
  Readonly<{ correlationId: string; jobReference: string }>,
  EvaluationRecoveryJob
>("evaluationRecovery:getJob");
export const cancelEvaluationRecovery = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    commandKey: string;
    jobReference: string;
    expectedRevision: number;
    reason: "operator-request" | "scope-no-longer-approved";
  }>,
  EvaluationRecoveryCommandResult
>("evaluationRecovery:cancel");
export const resumeEvaluationRecovery = makeFunctionReference<
  "mutation",
  Readonly<{
    correlationId: string;
    commandKey: string;
    jobReference: string;
    expectedRevision: number;
    reason: "incident-recovery" | "verification-after-repair";
  }>,
  EvaluationRecoveryCommandResult
>("evaluationRecovery:resume");

export const getNotificationHoursPolicy = makeFunctionReference<
  "query",
  { correlationId: string },
  NotificationHoursPolicy
>("notificationHours:getAdministrationPolicy");
export const setNotificationHoursPolicy = makeFunctionReference<
  "mutation",
  SetNotificationHours,
  NotificationHoursResult
>("notificationHours:setPolicy");
